import React from 'react';
import { Award, Target } from 'lucide-react';
import { mockData } from '../mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const AboutSection = () => {
  const { about } = mockData;
  const [imageRef, imageVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();

  return (
    <section id="about" className="relative py-12 md:py-24 bg-gradient-to-b from-black to-gray-900">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#5F9BC7]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-[#FFE7AB]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <div 
              ref={imageRef}
              className={`scroll-animate slide-in-left ${imageVisible ? 'visible' : ''} relative order-2 lg:order-1`}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-green-500/20">
                <img 
                  src={about.image} 
                  alt={about.name}
                  className="w-full h-[600px] object-cover"
                />
                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-[#5F9BC7] to-[#FFE7AB] rounded-2xl blur-2xl opacity-50"></div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-[#FFE7AB] to-[#5F9BC7] rounded-2xl blur-2xl opacity-50"></div>
            </div>

            {/* Content Side */}
            <div 
              ref={contentRef}
              className={`scroll-animate slide-in-right ${contentVisible ? 'visible' : ''} order-1 lg:order-2`}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFE7AB]/10 border border-[#FFE7AB]/30 backdrop-blur-sm mb-6">
                <Award className="w-4 h-4 text-[#FFE7AB]" />
                <span className="text-sm font-medium text-[#FFE7AB]">Your Coach</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {about.title}
              </h2>

              <div className="h-1 w-20 bg-gradient-to-r from-[#5F9BC7] to-[#FFE7AB] rounded-full mb-8"></div>

              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                {about.story}
              </p>

              {/* Credentials */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-400" />
                  Credentials & Experience
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {about.credentials.map((credential, index) => (
                    <div 
                      key={`credential-${credential.substring(0, 20)}-${index}`}
                      className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:border-green-500/50 transition-all duration-300"
                    >
                      <div className="w-2 h-2 rounded-full bg-green-400"></div>
                      <span className="text-gray-300 font-medium">{credential}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mission Statement */}
              <div className="mt-8 bg-gradient-to-br from-green-500/10 to-purple-500/10 backdrop-blur-sm border border-green-500/30 rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-green-400" />
                  My Mission
                </h4>
                <p className="text-gray-300 leading-relaxed">
                  To help you build a sustainable, healthy lifestyle that doesn't require giving up the things you love. 
                  Real transformation comes from consistency, not perfection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;

